import React, { ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useReactiveVar } from '@apollo/client';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import MenuIcon from '@material-ui/icons/Menu';

import { Bold } from '../_ui/typography.component';
import { SecondaryButton } from '../_ui/buttons.component';
import { treeFromPath, parentUser, buildSignOut } from '../../../lib/services/auth.service';
import { SessionType } from '../../../lib/types/session.types';
import { formatPhone } from '../../../lib/helpers/phone.helpers';
import { PARENT_ACCOUNT_ROUTE, PARENT_HOME_ROUTE } from '../../../../_configuration';
import { HeaderBar, HeaderSide, HeaderTitle, IdentitySubtitle } from './parent-header.styles';
import { ParentMobileMenu } from './parent-mobile-menu.component';

// Module-scope cross-mount nav tracker. ParentHeader remounts on every
// /parent/* navigation, so an instance ref can't see the immediately prior
// path; these survive remounts and let handleBack decide whether goBack()
// would unwind into the parent tree or eject out of it.
let trackedPriorPath: string | null = null;
let trackedCurrPath: string | null = null;
const noteNavigation = (pathname: string) => {
  if (trackedCurrPath === pathname) return;
  trackedPriorPath = trackedCurrPath;
  trackedCurrPath = pathname;
};

export interface IParentHeaderProps {
  // When set (and a parent is signed in), renders a back nav to this path.
  backTo?: string;
  backLabel?: string;
  // Page title rendered in the centre of the bar (desktop) or left (mobile).
  title?: ReactNode;
}

// Shared chrome for every page under /parent: full-bleed green strip with
// back nav, page title, parent identity, and sign-out. Desktop shows the
// identity + actions inline; mobile collapses them into a hamburger that
// opens a full-screen menu (see ParentMobileMenu). Identity is read from
// the `parentUser` reactive var so every parent route shows the same
// name/email/phone — call sites only pass title and back-nav. Renders
// nothing outside the parent tree.
export const ParentHeader: React.FC<IParentHeaderProps> = ({ backTo, backLabel = 'Back', title }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const parent = useReactiveVar(parentUser);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [menuOpen, setMenuOpen] = useState(false);
  const signOut = buildSignOut();

  useLayoutEffect(() => { noteNavigation(pathname); }, [pathname]);

  // goBack only when the prior entry is also in the parent tree — otherwise
  // the SPA push that brought us here came from outside /parent and goBack
  // would eject the user (e.g. back to the public-token URL they signed in
  // from). Falls back to a fresh push of backTo for deep links / reloads.
  const handleBack = useCallback(() => {
    if (!backTo) return;
    const priorWasParent = !!trackedPriorPath && treeFromPath(trackedPriorPath) === SessionType.PARENT;
    if (history.action === 'PUSH' && priorWasParent) history.goBack();
    else history.push(backTo);
  }, [backTo, history]);

  const handleAccount = useCallback(() => history.push(PARENT_ACCOUNT_ROUTE), [history]);
  const handleSignOut = useCallback(() => signOut(SessionType.PARENT, true), [signOut]);

  if (treeFromPath(pathname) !== SessionType.PARENT) return null;

  const displayName = parent?.name || 'My account';
  const subtitle = [parent?.email, formatPhone(parent?.phone)].filter(Boolean).join('  •  ');

  // Anonymous token visitors get a single sign-in affordance, same on both
  // breakpoints — there's no identity or sign-out to expose behind a menu.
  if (!parent) {
    return (
      <HeaderBar>
        <HeaderSide />
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderSide>
          <SecondaryButton variant="outlined" onClick={() => history.push(PARENT_HOME_ROUTE)}>
            See all my players
          </SecondaryButton>
        </HeaderSide>
      </HeaderBar>
    );
  }

  if (isMobile) {
    return (
      <>
        <HeaderBar>
          {backTo && (
            <IconButton
              aria-label={backLabel}
              onClick={handleBack}
              size="small"
              edge="start"
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          <HeaderTitle>{title}</HeaderTitle>
          <IconButton
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            size="small"
            edge="end"
          >
            <MenuIcon />
          </IconButton>
        </HeaderBar>
        <ParentMobileMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          parent={parent}
          onBack={backTo ? handleBack : undefined}
          backLabel={backLabel}
          onAccount={handleAccount}
          onSignOut={handleSignOut}
        />
      </>
    );
  }

  return (
    <HeaderBar>
      <HeaderSide>
        {backTo && (
          <SecondaryButton
            variant="outlined"
            startIcon={<ChevronLeftIcon fontSize="small" />}
            onClick={handleBack}
          >
            {backLabel}
          </SecondaryButton>
        )}
      </HeaderSide>

      <HeaderTitle>{title}</HeaderTitle>

      <HeaderSide>
        <Box textAlign="right">
          <Bold>{displayName}</Bold>
          {subtitle && <IdentitySubtitle>{subtitle}</IdentitySubtitle>}
        </Box>
        <Tooltip title="My account">
          <IconButton aria-label="My account" onClick={handleAccount} size="medium">
            <AccountCircleIcon />
          </IconButton>
        </Tooltip>
        <SecondaryButton variant="outlined" onClick={handleSignOut}>Sign out</SecondaryButton>
      </HeaderSide>
    </HeaderBar>
  );
};
